import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ShamCheckmarkIcon, ShamDeliveryTruck1Icon } from 'sham-components/icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WishlistService } from '@/app/services/wishlist.api';
import { MEDIA_BASE_URL as BASE_URL } from '@/environments/environment';
import { CartApi, CartItem } from '@/app/services/cart-api';
import { EmptyPage } from '@/app/layouts/empty-page/empty-page';
import { ToastService } from '@/app/services/http-error-notification.service';

interface FavoriteItem {
  id: string;
  productId: string;
  productSlug: string;
  variantId: string;
  storeName: string;
  storeLogoUrl: string;
  storeUrl: string;
  imageUrl: string;
  title: string;
  rating: number;
  reviewsCount: number;
  deliveryDate: number;
  hasDiscountOnCart: boolean;
  currentPrice: string;
  oldPrice: string;
  currency: string;
  primaryActionText: string;
  sellerId: string;
  availableQuantity: number;

  // secondaryActionType: 'dropdown' | 'button' | 'disabled-dropdown';
  // secondaryActionText: string;
  //  optionNames?: string[];
}

@Component({
  selector: 'app-wishlist',
  standalone: true,
  imports: [
    CommonModule,
    ShamDeliveryTruck1Icon,
    ShamCheckmarkIcon,
    TranslateModule,
    EmptyPage,
    RouterLink,
  ],
  templateUrl: './wishlist.html',
  styleUrl: './wishlist.css',
})
export class WishlistPage {
  private wishlistService = inject(WishlistService);
  private cartApi = inject(CartApi);
  private translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  items: FavoriteItem[] = [];
  isLoading = true;

  ngOnInit(): void {
    this.fetchWishlist();
    this.loadCart();
  }
  cartItems: CartItem[] = [];
  loadCart(): void {
    this.cartApi.getCart().subscribe({
      next: (res) => {
        this.cartItems = res.sellerGroups.flatMap((g) => g.items);
      },
    });
  }

  getCartQuantity(productId: string, variantId: string): number {
    const item = this.cartItems.find(
      (x) => x.productId === productId && x.variantId === variantId,
    );

    return item?.quantity ?? 0;
  }
  getRemainingStock(item: FavoriteItem): number {
    const inCart = this.getCartQuantity(item.productId, item.variantId);
    return item.availableQuantity - inCart;
  }
  fetchWishlist(): void {
    this.isLoading = true;
    this.wishlistService.getWishlist().subscribe({
      next: (response) => {
        if (!response || !response.items) {
          this.items = [];
          this.isLoading = false;
          return;
        }

        this.items = response.items.map((apiItem) => {
          let actionType: 'dropdown' | 'button' | 'disabled-dropdown' = 'button';
          let actionText = 'Standard';

          if (!apiItem.isAvailable || apiItem.availableQuantity === 0) {
            actionType = 'disabled-dropdown';
            actionText = 'Out of Stock';
          } else if (apiItem.options && apiItem.options.length > 0) {
            actionType = 'dropdown';
            actionText = apiItem.options[0].attributeDefinitionName || 'Options';
          } else {
            actionType = 'button';
            actionText = 'Standard';
          }

          return {
            id: apiItem.id,
            sellerId: apiItem.sellerId,
            productId: apiItem.productId,
            productSlug: apiItem.productSlug,
            variantId: apiItem.variantId,
            title: apiItem.productName,
            availableQuantity: apiItem.availableQuantity,
            storeName: apiItem.sellerName,
            imageUrl: this.resolveImageUrl(apiItem.thumbnailUrl),
            storeLogoUrl: this.resolveImageUrl(apiItem.sellerLogoUrl),
            storeUrl: `/store/${apiItem.sellerSlug}`,

            currentPrice:
              apiItem.isOnSale && apiItem.salePrice !== null
                ? apiItem.salePrice.toString()
                : apiItem.regularPrice.toString(),
            oldPrice: apiItem.isOnSale ? apiItem.regularPrice.toString() : '',
            currency: apiItem.regularPriceCurrency,
            hasDiscountOnCart: apiItem.isOnSale,

            rating: apiItem.averageReview || 0,
            reviewsCount: apiItem.reviewCount || 0,
            deliveryDate: apiItem.estimatedShippingDays || 0,
            primaryActionText: 'Add to cart',

            secondaryActionType: actionType,
            secondaryActionText: actionText,
            optionNames: apiItem.options
              ?.map((option) => option.attributeDefinitionName)
              .filter(Boolean) as string[],
          };
        });

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching wishlist', err);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  removeFromWishlist(itemId: string): void {
    this.items = this.items.filter((item) => item.id !== itemId);

    this.wishlistService.removeWishlistItem(itemId).subscribe({
      error: (err) => {
        console.error('Failed to remove item', err);
        this.fetchWishlist();
      },
    });
  }

  getStars(rating: number): number[] {
    return Array(5)
      .fill(0)
      .map((_, i) => i);
  }

  resolveImageUrl(path?: string | null): string {
    if (!path) return 'https://placehold.co/300x300';

    if (path.startsWith('http')) return path;

    const base = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    return `${base}${normalizedPath}`;
  }

  private showNotification(message: string, type: 'success' | 'error'): void {
    this.toast.show(message, type);
  }

  addToCart(item: FavoriteItem): void {
    const remaining = this.getRemainingStock(item);

    if (remaining <= 0) {
      this.showNotification(this.translate.instant('Out of stock'), 'error');
      return;
    }

    this.cartApi.addToCart(item.productId, item.variantId, 1).subscribe({
      next: (res) => {
        // 🔥 أهم خطوة: حدث cart locally بدون refresh
        this.loadCart();

        this.showNotification(this.translate.instant('Added to cart'), 'success');
      },
      error: (err) => {
        if (err?.error?.code === 'Cart.AddToCard') {
          this.showNotification(this.translate.instant('Not enough stock'), 'error');

          // optional: refresh cart to sync
          this.loadCart();
        }
      },
    });
  }

  openedDropdownId: string | null = null;

  toggleDropdown(itemId: string): void {
    this.openedDropdownId = this.openedDropdownId === itemId ? null : itemId;
  }
  private router = inject(Router);

  goToSellerProfile(sellerId: string) {
    if (!sellerId) return;
    this.router.navigate(['/seller', sellerId]);
  }
}
